/* eslint-disable complexity */
//constants
import {
	createFormProps,
	field,
	fieldMode,
	fieldType,
	fieldsGroup,
	formThemes,
	validationProps,
	dependencyAction,
	ignoreHiddenInputsTypes,
	fieldDependencyType,
	customValueTypes,
} from './types';
import { Box, ClickAwayListener, FormGroup, Stack, Typography } from '@mui/material';
import SelectBox from './inputs/SelectBox';
import SelectAutocomplete from './inputs/Autocomplete';
import Radio from './inputs/Radio';
import InputText from './inputs/InputText';
import DatePicker from './inputs/DatePicker';
import TextArea from './inputs/TextArea';
import DateRangePickerInput from './inputs/DateRangePickerInput';
import { useEffect, useState } from 'react';
import { FormEditButton } from 'theme/elements/Forms';
import { validateField, validateFields } from './validation';
import _ from 'lodash';
import { FileUpload, FileUploadBox } from './inputs/FIleUpload';
import CheckBox from './inputs/CheckBox';
import { dataType } from 'components/table/types';
import Custom from './inputs/Custom';
import { formatDate } from 'constants/global.constants';
import Label from './inputs/Label';
import { formatNumber } from './helpers';

export const getErrorText = (fields: { [key: string]: validationProps } = {}, name: string) => {
	if (_.has(fields, name)) {
		return _.get(fields, name).errorText;
	}
	return '';
};

export const isValid = (fields: { [key: string]: validationProps } = {}, name: string) => {
	if (_.has(fields, name)) {
		return _.get(fields, name).valid;
	}
	return true;
};

export const isValidForm = (fieldsGroups: fieldsGroup[], values: dataType, skipAmountValidation: boolean = false) => {
	let isValid = true;
	let totalInvalids = {};
	fieldsGroups.forEach((group, index2: number) => {
		const invalids = validateFields(group.fields, values || {}, skipAmountValidation);
		isValid = !_.isEmpty(invalids) ? false : isValid;
		totalInvalids = { ...totalInvalids, ...invalids };
	});
	return { isValid, invalids: totalInvalids };
};

export default function FormCreatorComponent(props: createFormProps): JSX.Element {
	// TOOD: change form creator to render children also so can render parts of form
	const [editMode, setEditMode] = useState({});
	const [values, setValues] = useState<Record<string, any> | undefined>();
	const [dirtyFields, setDirtyFields] = useState({});
	const [invalids, setInvalids] = useState(props.initialValidation || {});
	const formTheme = props.theme || formThemes.regular;
	const { checkInitValidation = true } = props;
	const getLabel = (field: field) => {
		let requiredLabel = '';
		const label = field.props.label;
		if (formTheme === formThemes.hiddenInputs && field.mode === fieldMode.readWithEnableWrite) {
			requiredLabel = '*';
		}
		return `${requiredLabel}${props.translation ? props.translation(label) : label}`;
	};
	const getWritableValue = (name: string) => {
		return _.has(dirtyFields, name) ? _.get(dirtyFields, name) : _.get(values, name);
	};

	const getValue = (field: field, readonly = false) => {
		const merged = _.merge(values, dirtyFields);
		let _value = _.get(merged, field.props.name);

		if (field.customValue) {
			return field.customValue(_value, merged);
		}

		_value = _.isNil(_value) || _value === '' ? (readonly ? 'n/a' : '') : _value;

		if (readonly && field.customValueType === customValueTypes.integer) {
			_value = formatNumber(_value);
		}

		if (readonly && field.suffix) {
			_value = field.suffix + _value;
		}

		if (readonly && field.type === 1 && _.isArray(field.props.options)) {
			//get label from options
			const option = _.find(field.props.options, { value: _value });
			if (option && props.translation) {
				return props.translation(option.label);
			}
		}
		return _value;
	};

	const ifNeedDependency = (field: field, value: any) => {
		if (field.dependency && field) {
			const dependencyValue = getWritableValue(field.dependency.name);
			const dependencyType = !_.has(field.dependency, 'type')
				? fieldDependencyType.regex
				: _.get(field.dependency, 'type');
			if (dependencyValue && dependencyType === fieldDependencyType.regex) {
				return !dependencyValue.match(field.dependency.value);
			} else {
				if (dependencyType === fieldDependencyType.isEmpty) {
					return _.isNil(dependencyValue);
				}
				if (dependencyType === fieldDependencyType.isCustomDependency && _.has(field, 'dependency.custom')) {
					return field.dependency.custom?.(values);
				}
				if (field.dependency.name === field.props.name) {
					return true;
				}
			}
		}
		return false;
	};

	const handleChangeField = (value: any, field: field) => {
		//start validate field
		const isValidField = validateField(field, value);
		const _dirtyFields = _.isEqual(_.get(values, field.props.name), value)
			? _.omit(dirtyFields, [field.props.name])
			: { ...dirtyFields, [field.props.name]: value };
		const _invalids = isValidField.valid
			? _.omit(invalids, [field.props.name])
			: { ...invalids, [field.props.name]: isValidField };

		let validForm = isValidField.valid;

		if (isValidField.valid) {
			validForm = isValidForm(props.fieldsGroups, { ...values, [field.props.name]: value }).isValid;
		}

		props.handleChange({
			dirtyFields: _dirtyFields,
			invalids: _invalids,
			isValid: validForm,
			isDirty: !_.isEmpty(_dirtyFields),
			values: { ...values, [field.props.name]: value },
			name: props.name,
		});
		setValues({ ...values, [field.props.name]: value });
		setDirtyFields(_dirtyFields);
		setInvalids(_invalids);
	};

	useEffect(() => {
		const formValues = props.values || {};
		setValues(formValues);
		if (!_.keys(formValues).length && props.emptyFormWhenValuesEmpty) {
			setDirtyFields({});
		}
		setInvalids(props.initialValidation || {});

		const validForm = isValidForm(props.fieldsGroups, props.values);
		if (checkInitValidation) {
			props.handleChange({
				isValid: validForm.isValid,
				invalids: validForm.invalids,
				name: props.name,
			});
		}
	}, [props.values, props.initialValidation]);

	useEffect(() => {
		if (_.isBoolean(props.isRequireValidate)) {
			const field = props.fieldsGroups[0].fields.find((field: any) => field.props.name.includes('amount'));

			if (field && _.has(values, field.props.name) && !_.isEmpty(values![field.props.name])) {
				let validForm;
				const isValidField = validateField(field, values![field.props.name]);
				if (isValidField.valid) {
					validForm = isValidForm(props.fieldsGroups, {
						...values,
						[field.props.name]: values![field.props.name],
					}).isValid;
				}
				const _dirtyFields = { ...dirtyFields, [field.props.name]: values![field.props.name] };
				const _invalids = { ...invalids, [field.props.name]: isValidField };
				setDirtyFields(_dirtyFields);
				setInvalids(_invalids);
				setValues(values);

				props.handleChange({
					dirtyFields: _dirtyFields,
					invalids: _invalids,
					isValid: validForm,
					isDirty: !_.isEmpty(_dirtyFields),
					values: { ...values, [field.props.name]: values![field.props.name] },
					name: props.name,
				});
			}
		}
	}, [props.isRequireValidate]);

	return (
		<>
			{_.isObject(values) && (
				<Stack gap={2}>
					{props.fieldsGroups.map((fieldGroup, i) => {
						const allFieldsHidden =
							fieldGroup.fields.length > 0 &&
							fieldGroup.fields.every((groupField) => {
								if (groupField.dependencyAction == dependencyAction.disable) {
									return false;
								}
								return ifNeedDependency(groupField, undefined);
							});
						if (allFieldsHidden) {
							return null;
						}
						return (
							<FormGroup
								key={`fieldgroup-${i}`}
								id={String(i)}
								sx={{ marginTop: i > 0 ? '0!important' : 'initial' }}
							>
								{fieldGroup.title && (
									<Typography
										variant={props.theme === formThemes.hiddenInputs ? 'subtitleBold' : 'boldBody1'}
										mb={1}
									>
										{props.translation(fieldGroup.title)}
									</Typography>
								)}
								<Stack
									sx={{
										display: 'flex',
										flexDirection: fieldGroup.type,
										...fieldGroup.styleObj,
									}}
									className={'fieldGroup-' + fieldGroup.type}
									gap={_.has(fieldGroup, 'spacing') ? fieldGroup.spacing : 2}
								>
									{fieldGroup.fields.map((cField, inx) => {
										const inputProps = {
											formTheme: formTheme,
											cField: {
												...cField,
												props: {
													...cField.props,
													label: getLabel(cField),
													value: getValue(cField),
												},
											},
											handleChange: (e) => {
												if (cField.type === 6) {
													handleChangeField(e.file || e?.target?.value || e, cField);
												} else {
													handleChangeField(e.file || e?.target?.value, cField);
												}
											},
											initialValidation: invalids,
											valid: isValid(invalids, cField.props.name),
										};
										const needDependency = ifNeedDependency(cField, inputProps.cField.props.value);
										if (needDependency) {
											if (cField.dependencyAction == dependencyAction.disable) {
												cField.props.disabled = true;
												cField.validation = [];
											} else {
												return <Box key={inx} display={'none'}></Box>;
											}
										}
										if (!needDependency) {
											if (cField.dependencyAction == dependencyAction.disable) {
												cField.props.disabled = false;
												cField.validation = [{ requierd: true }];
											}
										}

										return (
											<Box key={inx}>
												{formTheme === formThemes.hiddenInputs &&
													!ignoreHiddenInputsTypes.includes(cField.type) && (
														<Stack
															direction={'row'}
															justifyContent={'space-between'}
															position={'relative'}
														>
															{!_.isEmpty(cField.props.label) && (
																<Typography
																	variant="subtitleBold"
																	color={
																		_.has(invalids, cField.props.name)
																			? 'error'
																			: ''
																	}
																>
																	{getLabel(cField)}
																</Typography>
															)}
															{cField.mode === fieldMode.readWithEnableWrite && (
																<FormEditButton
																	className={
																		_.get(editMode, cField.props.name) && 'active'
																	}
																>
																	<img
																		className="editButton"
																		onClick={() => {
																			const mode = _.get(
																				editMode,
																				cField.props.name
																			)
																				? false
																				: true;
																			setEditMode({
																				...editMode,
																				[cField.props.name]: mode,
																			});
																		}}
																		src={'/icons/pencil2.svg'}
																	/>
																</FormEditButton>
															)}
														</Stack>
													)}
												{formTheme === formThemes.hiddenInputs &&
													!ignoreHiddenInputsTypes.includes(cField.type) &&
													!_.get(editMode, cField.props.name) && (
														<Typography variant="subtitle2">
															{getValue(cField, true)}
														</Typography>
													)}
												{(formTheme === formThemes.regular ||
													_.get(editMode, cField.props.name) ||
													ignoreHiddenInputsTypes.includes(cField.type)) && (
													<>
														{cField.type === fieldType.text && (
															<InputText {...inputProps} />
														)}
														{cField.type === fieldType.select && (
															<SelectAutocomplete {...inputProps} />
														)}
														{cField.type === fieldType.selectBox && (
															<SelectBox {...inputProps} />
														)}
														{cField.type === fieldType.radio && <Radio {...inputProps} />}
														{cField.type === fieldType.checkbox && (
															<CheckBox {...inputProps} />
														)}
														{cField.type === fieldType.datePicker && (
															<DatePicker {...inputProps} />
														)}
														{cField.type === fieldType.textArea && (
															<TextArea {...inputProps} />
														)}
														{cField.type === fieldType.dateRangePicker && (
															<DateRangePickerInput {...inputProps} />
														)}
														{cField.type === fieldType.file && (
															<FileUploadBox
																translation={props.translation}
																{...inputProps}
															/>
														)}
														{cField.type === fieldType.custom && <Custom {...inputProps} />}
														{cField.type === fieldType.label && <Label {...inputProps} />}
													</>
												)}
											</Box>
										);
									})}
								</Stack>
							</FormGroup>
						);
					})}
				</Stack>
			)}
		</>
	);
}
